import { Select, MenuItem, SelectChangeEvent, Radio, FormControl, Tooltip, TooltipProps, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import Expand from "@/app/csr-src/assets/icons/chevron-down.svg";
import { AddressVo, ReturnCustomerInfoVo } from "@/app/csr-src/apis/afterSales/afterSales.interface";
import AddressAdd from "@/app/csr-src/assets/icons/AddressAdd.svg";
import styles from "./index.module.scss";

const CustomIcon: React.FC = (props) => <Expand {...props} />;

interface addressSelectProps {
  selectValue: string;
  selectList: AddressVo[] | [];
  selectChangeHandler: (value: string) => void;
  placeholder?: string;
  addAddressClick: () => void;
  defaultAddress?: ReturnCustomerInfoVo | null;
  useDefault: boolean;
}

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} arrow classes={{ popper: className }} />)(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}));

const addressSelect: React.FC<addressSelectProps> = observer(
  ({ selectValue, selectList, selectChangeHandler, placeholder, addAddressClick, defaultAddress, useDefault }) => {
    const { t } = useTranslation();

    // if (!selectList?.length) {
    //   return null;
    // }
    const handleChange = (event: SelectChangeEvent) => {
      selectChangeHandler && selectChangeHandler(event.target.value);
    };

    const handleClick = (value: string) => {
      if (value === selectValue) {
        handleChange({ target: { value } } as SelectChangeEvent); // 手动触发 onChange
      }
    };

    return (
      <div className={styles.select_wrap}>
        <FormControl fullWidth={true}>
          <Select
            value={selectValue}
            onChange={handleChange}
            displayEmpty
            inputProps={{ "aria-label": "Without label" }}
            renderValue={(value: string) => {
              if (value) {
                const selected = selectList.find((item: AddressVo) => String(item.addressId) === value);
                // 兼容地址被编辑与删除场景
                const contactName =
                  !useDefault || (selected?.name && selected?.name === defaultAddress?.contactName) ? selected?.name : defaultAddress?.contactName;
                const contactTel =
                  !useDefault || (selected?.mobile && selected?.mobile === defaultAddress?.contactTel) ? selected?.mobile : defaultAddress?.contactTel;
                const address =
                  !useDefault || (selected?.fullAddress && selected?.fullAddress === defaultAddress?.addressInfo?.address)
                    ? selected?.fullAddress
                    : defaultAddress?.addressInfo?.address;
                return (
                  <>
                    <div className={styles.address_main}>
                      <div className={styles.address_name}>{contactName}</div>
                      <div className={styles.address_phone}>
                        {selected?.areaCode ? `+${selected?.areaCode}` : `+${selectList[0]?.areaCode}`} {contactTel}
                      </div>
                    </div>
                    <div className={styles.address_detail}>{address}</div>
                  </>
                );
              } else {
                return <span className={styles.placeholder_style}>{placeholder}</span>;
              }
            }}
            IconComponent={CustomIcon}
            MenuProps={{
              anchorOrigin: {
                vertical: "top", // 菜单的垂直锚点
                horizontal: "left", // 菜单的水平锚点
              },
              transformOrigin: {
                vertical: "bottom", // 菜单的变换点
                horizontal: "left", // 菜单的变换点
              },
              PaperProps: {
                style: {
                  maxHeight: 286, // 设置下拉菜单的最大高度
                  overflowY: "auto", // 允许垂直滚动
                },
              },
            }}
          >
            {selectList?.map((item: AddressVo, index: number) => {
              return (
                <MenuItem
                  value={String(item.addressId)}
                  key={index}
                  // sx={{ paddingTop: "7px", paddingBottom: "7px", marginBottom: "8px" }}
                  sx={{
                    padding: "7px 12px 7px 19px",
                    marginBottom: "8px",
                    fontSize: "14px",
                    ".MuiSvgIcon-fontSizeSmall": {
                      fontSize: "16px",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "transparent",
                    },
                    "&:first-child": {
                      marginTop: "4px",
                    },
                    "&:last-child": {
                      marginBottom: "4px",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  onClick={() => {
                    handleClick(String(item.addressId));
                  }}
                >
                  <Radio checked={String(item.addressId) === selectValue} size="small" />
                  <div className={styles.address_wrap}>
                    <div className={styles.address_main}>
                      <div className={styles.address_name}>{item?.name}</div>
                      <div className={styles.address_phone}>
                        {item?.areaCode ? "+" + item?.areaCode : ""} {item?.mobile}
                      </div>
                    </div>
                    {/* <div className={styles.address_detail}>{item?.fullAddress}</div> */}
                    <BootstrapTooltip title={item.fullAddress} placement="top" arrow={true}>
                      <div className={styles.address_detail}>{item?.fullAddress}</div>
                    </BootstrapTooltip>
                  </div>
                </MenuItem>
              );
            })}
            {/* 新增地址，todo 是否可以考虑组织冒泡，不关闭下拉菜单 */}
            <MenuItem
              sx={{
                fontSize: "14px",
              }}
            >
              <div
                className={styles.add_address_wrap}
                onClick={() => {
                  addAddressClick();
                }}
              >
                <AddressAdd className={styles.add_icon} />
                {t("order.orderDetail.returnRequestModal.addAddressTip")}
              </div>
            </MenuItem>
          </Select>
        </FormControl>
      </div>
    );
  },
);

export default addressSelect;
